import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Column, TableAction } from 'components/display/Table/types';
import { Button } from 'components/ui/Button';
import { EyeIcon } from 'components/icons/Icons';
import { CheckCircle2, RefreshCw, XCircle } from 'lucide-react';
import ServerTable from 'shared/contexts/table-context/components/ServerTable';
import {
  FacetQueryCommand,
  mapFacetFiltersToFiltersDto
} from 'shared/contexts/table-context/facetFilters';
import { buildExportCommand, exportListCsv } from 'shared/contexts/table-context/exportCsv';
import { OnCreateConfig } from 'shared/contexts/table-context/components/types';
import { LinkPlateformesAction } from 'back-office/shared/form/LinkPlateformesAction';
import { productsAPI } from 'services/swagger-api';
import { ProduitAdminListItem } from 'api/product';
import { TableResponse } from 'slices/generic-table/genericTableSlice';

interface ProduitsAdminListProps {
  createConfig?: OnCreateConfig;
  /** Bump from the outside to refetch the list, e.g. once a product was created. */
  refreshToken?: number;
}

const fmtDate = (iso?: string | Date | null): string =>
  iso ? new Date(iso).toLocaleDateString('fr-FR') : '—';

const activeBadge = (isActive: boolean) =>
  isActive ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
      <CheckCircle2 className="h-3.5 w-3.5" />
      Actif
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-500">
      <XCircle className="h-3.5 w-3.5" />
      Inactif
    </span>
  );

export default function ProduitsAdminList({
  createConfig,
  refreshToken = 0
}: ProduitsAdminListProps) {
  const navigate = useNavigate();
  // Bump pour refetch la liste après (dé)liaison de plateformes
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const columns: Column<ProduitAdminListItem>[] = useMemo(
    () => [
      {
        key: 'logoURL',
        label: 'Logo',
        render: (v) =>
          v ? (
            <img src={v as string} alt="" className="mx-auto h-7 w-7 rounded object-contain" />
          ) : (
            <span className="text-gray-300">—</span>
          )
      },
      {
        key: 'code',
        label: 'Code',
        sortKey: 'identifier',
        render: (v) => <span className="font-mono text-xs text-gray-600">{v as string}</span>
      },
      {
        key: 'label',
        label: 'Libellé',
        sortKey: 'label',
        render: (v) => <span className="font-semibold text-gray-900">{v as string}</span>
      },
      {
        key: 'branchLabel',
        label: 'Branche',
        sortKey: 'branch',
        render: (v, row) => (
          <div>
            <div className="text-sm text-gray-900">{(v as string) || '—'}</div>
            {row.subBranchLabel && (
              <div className="text-xs text-gray-500">{row.subBranchLabel}</div>
            )}
          </div>
        )
      },
      {
        key: 'ownerName',
        label: 'Propriétaire',
        sortKey: 'owner',
        render: (v) => <span className="text-sm text-gray-700">{(v as string) || '—'}</span>
      },
      {
        key: 'isTacitRenewal',
        label: 'Tacite reconduction',
        render: (v) =>
          v ? (
            <span className="inline-flex items-center gap-1 text-sm text-gray-700">
              <RefreshCw className="h-3.5 w-3.5 text-primary" />
              Oui
            </span>
          ) : (
            <span className="text-sm text-gray-400">Non</span>
          )
      },
      {
        key: 'quotesCount',
        label: 'Devis',
        sortKey: 'quoteCount',
        render: (v) => <span className="text-sm text-gray-700">{v as number}</span>
      },
      {
        key: 'activeContractsCount',
        label: 'Contrats',
        sortKey: 'contractCount',
        render: (v) => <span className="text-sm text-gray-700">{v as number}</span>
      },
      {
        key: 'isActive',
        label: 'Statut',
        render: (v) => activeBadge(v as boolean)
      },
      {
        key: 'creationDate',
        label: 'Créé le',
        sortKey: 'date',
        render: (v) => <span className="text-sm text-gray-700">{fmtDate(v as string)}</span>
      }
    ],
    []
  );

  const fetchProduits = useCallback(
    async (params: any): Promise<TableResponse<ProduitAdminListItem>> => {
      const result = await productsAPI.getAdminProduits({
        getGenericListCommandDto: {
          pageNumber: params.pageNumber,
          pageSize: params.pageSize,
          searchString: params.searchString || undefined,
          sortBy: params.sortBy,
          sortDirection: params.sortDirection,
          filters: mapFacetFiltersToFiltersDto(params.filters)
        }
      });
      const items = result.items || [];
      return {
        items,
        totalItems: result.totalCount || items.length,
        totalPages: Math.ceil((result.totalCount || items.length) / params.pageSize),
        currentPage: params.pageNumber
      };
    },
    []
  );

  const fetchFacets = useCallback(
    (command: FacetQueryCommand) =>
      productsAPI.getAdminProduitListFacets({ getGenericListCommandDto: command }),
    []
  );

  const rowActions: TableAction<ProduitAdminListItem>[] = [
    {
      label: 'view',
      render: (row, index) => (
        <Button
          key={index}
          color="slate"
          round
          onClick={() => navigate(`/back-office/produits/${row.id}`)}
        >
          <EyeIcon />
        </Button>
      )
    },
    {
      // Lier des plateformes au produit. La ligne ne porte pas les plateformes :
      // on les charge à l'ouverture pour présélectionner (la commande remplace le set).
      label: 'link-plateformes',
      render: (row, index) => (
        <LinkPlateformesAction
          key={index}
          title={`Plateformes du produit · ${row.label}`}
          resolveInitialCodes={async () => {
            const res = await productsAPI.getPlateformes({ productId: row.id });
            return res.plateformes.map((o) => o.code);
          }}
          onLink={(plateformeIds) =>
            productsAPI
              .linkPlateformes({
                productId: row.id,
                genericLinkPlateformesCommand: { plateformeIds }
              })
              .then(() => undefined)
          }
          onSuccess={() => setRefreshTrigger((t) => t + 1)}
        />
      )
    }
  ];

  // Rendu « nu » : le layout + le header (bandeau blanc) sont fournis par ProductsView
  return (
    <ServerTable<ProduitAdminListItem>
      apiConfig={{ fetchData: fetchProduits }}
      columns={columns}
      selectable
      serverSorting
      centerColumns
      columnCustomization={{ storageKey: 'bo-produits' }}
      refreshTrigger={refreshTrigger + refreshToken}
      facets={{ fetchFacets }}
      onExport={(p) =>
        exportListCsv(
          '/api/backoffice/produits/admin/liste/csv',
          buildExportCommand(p, mapFacetFiltersToFiltersDto(p.filters))
        )
      }
      actions={{ ...(createConfig && { create: createConfig }), row: rowActions }}
    />
  );
}
